<template>
  <div class="bg-white p-4">
    <div class="row mb-4">
      <div class="col-12">
        <div class="header">
          <span class="header-icon">
            <i class="fa fa-download" aria-hidden="true"></i>
          </span>
          <h2>{{ $t('downloads.pageTitle') }}</h2>
          <span
            class="ml-3 refresh-icon"
            :title="`${this.$t('dashboard.updateList')}`"
            @click="refreshTable"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </span>
        </div>
      </div>
    </div>

    <div>
      <b-table striped :items="items" :fields="fields" :busy="loading">
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner type="grow" class="align-middle"></b-spinner>
          </div>
        </template>
        <template v-slot:cell(createdAt)="row">
          <div>
            {{ dateFormat(row.item.createdAt) }}
          </div>
        </template>

        <template v-slot:cell(type)="row">
          <div>
            {{ translateType(row.item.type) }}
          </div>
        </template>

        <template v-slot:cell(status)="row">
          <div :class="`flag-status flag-status-${row.item.status}`">
            {{ row.item.status.toUpperCase() }}
          </div>
        </template>

        <template v-slot:cell(file)="row">
          <loader v-if="row.item.status === 'downloading'" />
          <button
            v-else-if="row.item.status === 'concluido'"
            class="download-icon"
            title="Fazer download"
            @click="getFile(row.item, row.index)"
            :disabled="row.item.status === 'downloading'"
          >
            <i class="fa fa-download" aria-hidden="true"></i>
          </button>
          <span v-else>
            -
          </span>
        </template>
      </b-table>
    </div>

    <div class="pagination-area-clients mt-1">
      <paginate
        v-model="page"
        :clickHandler="getDownloads"
        :pageCount="Math.ceil(total / 10)"
        :prevText="'‹'"
        :nextText="'›'"
        :activeClass="'active'"
        :containerClass="'pagination b-pagination'"
        :pageClass="'page-item'"
      ></paginate>
    </div>
  </div>
</template>

<script>
import Paginate from 'vuejs-paginate';
import { format } from 'date-fns';
import swal from 'sweetalert';
import api from '@/services/api';
import loader from '@/components/atoms/loader-download';

export default {
  components: {
    Paginate,
    loader,
  },

  data: () => ({
    loading: false,
    userAuth: null,
    total: 0,
    page: 1,
    limit: 10,
    fields: [],
    items: [],
  }),

  methods: {
    dateFormat(data) {
      return format(new Date(data), 'dd/MM/yyyy HH:mm');
    },

    translateType(type) {
      if (type === 'clients') {
        return this.$t('downloads.clients');
      } else if (type === 'answers') {
        return this.$t('downloads.answers');
      } else if (type === 'invites') {
        return this.$t('downloads.invites');
      } else if (type === 'invites-status') {
        return this.$t('downloads.invitesStatus');
      } else if (type === 'answers-indicators') {
        return this.$t('downloads.answerIndicators');
      } else if (type === 'reminders') {
        return this.$t('downloads.reminders');
      } else if (type === 'scheduling') {
        return this.$t('downloads.scheduling');
      } else if (type === 'trash') {
        return this.$t('downloads.trash');
      } else if (type === 'sentiment-analysis') {
        return this.$t('sentiment.pageTitle');
      } else if (type === 'quarentine') {
        return this.$t('downloads.quarentine');
      } else if (type === 'blacklist') {
        return this.$t('downloads.blackList');
      } else if (type === 'analytics') {
        return this.$t('downloads.analytics');
      } else if (type === 'category-input') {
        return this.$t('downloads.category');
      } else if (type === 'consumption') {
        return this.$t('downloads.consumption');
      } else if (type === 'answers-details') {
        return this.$t('downloads.answerDetails');
      } else if (type === 'action') {
        return this.$t('downloads.action');
      }
      return '-';
    },

    async getFile(download, index) {
      this.items[index].status = this.$t('downloads.downloading');

      this.apiServerlessDownloads = process.env.API_SERVERLESS_DOWNLOADS;

      try {
        const link = window.document.createElement('a');
        let response = null;

        if (
          download.type === 'clients' ||
          download.type === 'answers' ||
          download.type === 'answers-indicators' ||
          download.type === 'quarentine' ||
          download.type === 'blacklist' ||
          download.type === 'sentiment-analysis' ||
          download.type === 'analytics' ||
          download.type === 'category-input' ||
          download.type === 'consumption' ||
          download.type === 'action'
        ) {
          response = await api.get(
            `${this.apiServerlessDownloads}/download/${download._id}`,
            {
              headers: { 'x-access-token': this.userAuth.token },
            },
          );

          if (response.data) {
            link.href = response.data;
          } else {
            swal({
              title: this.$t('downloads.invalidDownload.title'),
              text: this.$t('downloads.invalidDownload.description'),
              icon: 'error',
            });
          }
        } else {
          response = await api.get(`/download/${download._id}`, {
            responseType: 'blob',
            headers: { 'x-access-token': this.userAuth.token },
          });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          link.href = url;
          link.setAttribute(
            'download',
            `${download.type}.${download.fileExtension}`,
          );
        }

        document.body.appendChild(link);

        link.click();
      } catch (error) {
        let errorMessage = this.$t('downloads.internalError');

        if (error.response.status === 400) {
          errorMessage = this.$t('downloads.fileNotFound');
        }

        swal({
          title: this.$t('downloads.errorTitle'),
          text: errorMessage,
          icon: 'error',
        });
      }

      this.items[index].status = this.$t('downloads.finished');
    },

    async getDownloads() {
      try {
        this.loading = true;

        const response = await api.get(`downloads/${this.userAuth.companyId}`, {
          headers: { 'x-access-token': this.userAuth.token },
          params: { page: this.page, limit: this.limit },
        });

        this.items = response.data.availableDownloads;
        this.total = response.data.total;
      } catch (error) {
        swal({
          title: this.$t('downloads.errorTitle'),
          text: error.response.data.message,
          icon: 'error',
        });
      }

      this.loading = false;
    },

    async refreshTable() {
      this.page = 1;

      await this.getDownloads();
    },
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    this.fields = [
      {
        key: 'controlId',
        label: this.$t('downloads.downloadsTable.labels.id'),
      },
      {
        key: 'createdAt',
        label: this.$t('downloads.downloadsTable.labels.date'),
      },
      { key: 'type', label: this.$t('downloads.downloadsTable.labels.type') },
      {
        key: 'fileExtension',
        label: this.$t('downloads.downloadsTable.labels.extension'),
      },
      {
        key: 'status',
        label: this.$t('downloads.downloadsTable.labels.status'),
      },
      { key: 'file', label: this.$t('downloads.downloadsTable.labels.file') },
    ];
  },

  async mounted() {
    await this.getDownloads();
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';
@import '@/themes/default/placeholders.scss';

h2 {
  text-transform: uppercase;
  font-size: 21px;
  font-weight: 100;
  color: $gray;
}

.header {
  display: flex;
  flex-direction: row;

  .header-icon {
    float: left;
    font-size: 12px;
    margin-top: 4px;
    padding-right: 12px;
    color: $gray;
  }

  .refresh-icon {
    margin-top: 1px;
    color: $primary;
    cursor: pointer;
    &:hover {
      color: green;
    }
  }
}

.flag-status {
  padding: 0.2rem;
  font-size: 0.8rem;
  border-radius: 0.25rem;
  color: $white;
  width: 100px;
  text-align: center;
  opacity: 0.9;
}

.flag-status-concluido {
  background-color: #28a745;
}

.flag-status-processando {
  background-color: #ffc107;
}

.flag-status-expirado {
  background-color: $gray;
}

.flag-status-falha {
  background-color: red;
}

.download-icon {
  padding: 5px 15px;
  background: #28a745;
  color: $white;
  border-radius: 10px;
  font-size: 15px;
  border: none;
}

.download-icon:hover {
  cursor: pointer;
}

.pagination-area-clients >>> a {
  position: relative;
  display: block;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  color: #4ce595;
  background-color: #fff;
  border: 1px solid #dee2e6;
}
</style>

<style>
.pagination-area-clients .active a {
  text-decoration: none;
  z-index: 1;
  color: #fff;
  background-color: #4ce595;
  border-color: #4ce595;
}

.pagination-area-clients a {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/organisms/download/index.vue